/**
 * useDebounce Hook
 * Debounces a value with a configurable delay
 */

import { useState, useEffect } from "react";

/**
 * Debounces a value by the specified delay
 * @param value The value to debounce
 * @param delay The delay in milliseconds (default: 400ms)
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay: number = 400): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Debounced search with automatic page reset
 * Returns the debounced search term and a setter that also resets pagination
 */
export function useDebouncedSearch(delay: number = 400) {
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const debouncedSearch = useDebounce(searchInput, delay);

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  return {
    searchInput,
    setSearchInput,
    debouncedSearch,
    page,
    setPage,
  };
}

export default useDebounce;
